import cloneDeep from 'lodash-es/cloneDeep';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import { IconArrowDown16, IconCancel16, IconRefresh16 } from 'components/base/Icon';
import { useSearchQueryString } from 'hooks/useSearchQueryString';
import { gray500, gray600, gray_active } from 'styles';
import { getPriceKo } from 'util/search_utils';
import { Log } from 'util/Tracker';

export enum SEARCH_OPTION_TYPE {
  CATEGORY = 'CATEGORY',
  COLOR = 'COLOR',
  PRICE = 'PRICE',
  SHIPPING = 'SHIPPING',
  ZIGIN_SHIPPING = 'ZIGIN_SHIPPING',
  FREE_SHIPPING = 'FREE_SHIPPING',
  DELIVERY = 'DELIVERY',
  SORT = 'SORT',
  REFRESH = 'REFRESH',
}

export interface SearchOptionChip {
  name: string;
  type: SEARCH_OPTION_TYPE;
  classname?: string;
  active?: boolean;
  icon?: ReactNode;
  chip_ubl: Pick<Log, 'object_id' | 'object_idx'>;
}

const refresh_option_chip: SearchOptionChip = {
  name: '초기화',
  type: SEARCH_OPTION_TYPE.REFRESH,
  active: false,
  classname: 'reset',
  icon: <IconRefresh16 color={gray600} />,
  chip_ubl: { object_id: '초기화', object_idx: 0 },
};

/**
 * 검색필터에 노출되는 chip 들을 render 하기 위한 data 정제 hook
 * @hook
 */
const useSearchChips = (is_show_category?: boolean) => {
  const router = useRouter();
  const { search_props } = useSearchQueryString();

  // @ts-ignore
  const handleFilterResetClick = (e) => {
    e.stopPropagation();
    const type = e.target.dataset.type;
    const replace_query_string = cloneDeep(router.query);

    switch (type) {
      case 'color': {
        delete replace_query_string.color_list;
        break;
      }
      case 'category': {
        delete replace_query_string.category_list;
        break;
      }
      case 'price': {
        delete replace_query_string.min_price;
        delete replace_query_string.max_price;
        break;
      }
      case 'delivery': {
        delete replace_query_string.is_free_shipping;
        break;
      }
    }

    router.replace({ pathname: router.pathname, query: replace_query_string });
  };

  const is_selcted_filter = {
    color: search_props.color_list && search_props.color_list.length > 0,
    category: search_props.category_list && search_props.category_list.length > 0,
    price: !!search_props.price?.max,
    delivery: search_props.is_free_shipping,
  };

  const default_option_chips: SearchOptionChip[] = [
    {
      name: getCategortyName(search_props.category_list),
      type: SEARCH_OPTION_TYPE.CATEGORY,
      active: is_selcted_filter.category,
      icon: is_selcted_filter.category ? (
        <IconCancel16 onClick={handleFilterResetClick} data-type='category' color={gray_active} />
      ) : (
        <IconArrowDown16 color={gray500} />
      ),
      chip_ubl: { object_id: '카테고리', object_idx: 6 },
    },
    {
      name: getColorFitlerName(search_props.color_list),
      type: SEARCH_OPTION_TYPE.COLOR,
      active: is_selcted_filter.color,
      icon: is_selcted_filter.color ? (
        <IconCancel16 onClick={handleFilterResetClick} data-type='color' color={gray_active} />
      ) : (
        <IconArrowDown16 color={gray500} />
      ),
      chip_ubl: { object_id: '색상', object_idx: 4 },
    },
    {
      name: getPriceFilterName({ min: search_props.price?.min, max: search_props.price?.max }),
      type: SEARCH_OPTION_TYPE.PRICE,
      active: is_selcted_filter.price,
      icon: is_selcted_filter.price ? (
        <IconCancel16 onClick={handleFilterResetClick} data-type='price' color={gray_active} />
      ) : (
        <IconArrowDown16 color={gray500} />
      ),
      chip_ubl: { object_id: '가격', object_idx: 3 },
    },
    {
      name: is_selcted_filter.delivery ? '무료배송' : '배송',
      type: SEARCH_OPTION_TYPE.DELIVERY,
      active: is_selcted_filter.delivery,
      icon: is_selcted_filter.delivery ? (
        <IconCancel16 onClick={handleFilterResetClick} data-type='delivery' color={gray_active} />
      ) : (
        <IconArrowDown16 color={gray500} />
      ),
      chip_ubl: { object_id: '배송', object_idx: 5 },
    },
  ];

  const option_chips = is_show_category
    ? default_option_chips
    : default_option_chips.filter((chip) => chip.chip_ubl.object_id !== '카테고리');

  const is_selected_filter_except_sort = option_chips.some(
    (option) => option.type !== SEARCH_OPTION_TYPE.SORT && option.active === true,
  );

  return {
    search_option_chips: is_selected_filter_except_sort ? [refresh_option_chip, ...option_chips] : option_chips,
  };
};

export default useSearchChips;

function getCategortyName(category_list?: Array<{ id: string; name: string }>) {
  if (!category_list || category_list.length === 0) {
    return '카테고리';
  }

  const first_category = category_list[0];

  if (category_list.length === 1) {
    return first_category.name;
  }

  return `${first_category.name} 외 ${category_list.length - 1}`;
}

function getColorFitlerName(color_list: undefined | string[]) {
  if (!color_list || color_list.length === 0) {
    return '색상';
  }

  const first_color_name = color_list[0];

  if (color_list.length === 1) {
    return first_color_name;
  }

  return `${first_color_name} 외 ${color_list.length - 1}`;
}

function getPriceFilterName({ min, max }: { min?: number; max?: number }) {
  if (min !== undefined && max !== undefined) {
    return getPriceKo(min, max);
  }

  return '가격';
}
